import { motion } from 'framer-motion';
import { Award, Newspaper } from 'lucide-react';
import { Link } from 'react-router-dom';

const RecognitionPage = () => {
  const awards = [
    {
      title: 'Best AI Content Studio',
      year: '2024',
      org: 'Indian Tech Awards',
    },
    {
      title: 'Innovation in Digital Marketing',
      year: '2023',
      org: 'Marketing Excellence Awards',
    },
    {
      title: 'Top 10 Emerging Tech Companies',
      year: '2023',
      org: 'Business Today',
    },
    {
      title: 'Excellence in AI Implementation',
      year: '2024',
      org: 'AI Summit India',
    },
    {
      title: 'Best Creative Agency',
      year: '2024',
      org: 'Pune Business Awards',
    },
    {
      title: 'Outstanding Client Satisfaction',
      year: '2023',
      org: 'Customer Choice Awards',
    },
  ];

  const press = [
    {
      publication: 'Tech Crunch India',
      headline: 'Appdroid Tech revolutionizes AI content creation',
      date: 'March 2024',
      link: '/blog/tech-crunch-ai-revolution',
    },
    {
      publication: 'Business Standard',
      headline: 'Fusion Scope: The future of brand storytelling',
      date: 'January 2024',
      link: '/blog/business-standard-fusion-scope',
    },
    {
      publication: 'Economic Times',
      headline: 'How Pune-based startup is transforming digital marketing',
      date: 'November 2023',
      link: '/blog/economic-times-pune-startup',
    },
    {
      publication: 'YourStory',
      headline: 'From code to cinema: The Appdroid Tech journey',
      date: 'September 2023',
      link: '/blog/yourstory-code-to-cinema',
    },
  ];

  return (
    <div data-testid="recognition-page" className="min-h-screen">
      {/* Hero with Video Background */}
      <section className="min-h-screen flex items-center justify-center relative overflow-hidden">
        {/* Video Background */}
        <div className="absolute inset-0 w-full h-full">
          <video
            autoPlay
            loop
            muted
            playsInline
            className="absolute inset-0 w-full h-full object-cover"
            style={{ filter: 'brightness(0.6)' }}
          >
            <source
              src="https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/BigBuckBunny.mp4"
              type="video/mp4"
            />
          </video>
          {/* Gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70" />
          <div
            className="absolute inset-0 opacity-30"
            style={{
              background:
                'radial-gradient(circle at 50% 50%, rgba(124, 58, 237, 0.2) 0%, rgba(10, 10, 10, 0) 70%)',
            }}
          />
        </div>

        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-6xl md:text-8xl font-black tracking-tighter leading-none uppercase mb-6 text-white drop-shadow-2xl">
              Awards &
              <br />
              <span className="text-purple-400">Features</span>
            </h1>
            <p className="text-xl md:text-2xl text-white/80 max-w-2xl drop-shadow-lg">
              Recognition that validates our commitment to excellence.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Awards */}
      <section className="pb-24 md:pb-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <div className="flex items-center gap-4 mb-6">
              <Award className="text-purple-500" size={32} />
              <h2 className="text-3xl md:text-5xl font-extrabold tracking-tight">Awards & Accolades</h2>
            </div>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {awards.map((award, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                whileHover={{ scale: 1.02 }}
                data-testid={`award-card-${index}`}
                className="bg-card border border-themed rounded-2xl p-8 hover:border-purple-500/50 transition-all duration-500 group"
              >
                <div className="w-12 h-12 bg-purple-500/20 rounded-full flex items-center justify-center text-purple-500 mb-6">
                  <Award size={24} />
                </div>
                <div className="text-xs font-bold tracking-[0.2em] uppercase text-purple-500 mb-3">
                  {award.year}
                </div>
                <h3 className="text-xl font-bold mb-2 leading-tight">{award.title}</h3>
                <p className="text-sm text-muted">{award.org}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Press & Media */}
      <section className="py-24 md:py-32 bg-card">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <div className="flex items-center gap-4 mb-6">
              <Newspaper className="text-purple-500" size={32} />
              <h2 className="text-3xl md:text-5xl font-extrabold tracking-tight">Press & Media</h2>
            </div>
          </motion.div>

          <div className="flex flex-col gap-6">
            {press.map((item, index) => (
              <Link
                key={index}
                to={item.link}
                data-testid={`press-item-${index}`}
                className="block"
              >
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ scale: 1.01 }}
                  className="bg-card border border-themed rounded-2xl p-8 hover:border-purple-500/50 transition-all duration-500 group cursor-pointer"
                >
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex-1">
                      <div className="text-xs font-bold tracking-[0.2em] uppercase text-purple-500 mb-2">
                        {item.publication}
                      </div>
                      <h3 className="text-xl font-bold mb-2 group-hover:text-purple-400 transition-colors text-primary">
                        {item.headline}
                      </h3>
                      <p className="text-sm text-muted">{item.date}</p>
                    </div>
                    <div className="text-purple-500 group-hover:translate-x-2 transition-transform">
                      <span className="font-bold">Read More →</span>
                    </div>
                  </div>
                </motion.div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default RecognitionPage;